import React, { useState } from "react";
import { useNavigate } from "react-router";

import { t } from "i18next";

import { Check, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography
} from "@mui/material";

import { notificationAtom } from "@/src/state/notificationAtom";

import NotificationItem from "@/src/components/molecules/NotificationItem/NotificationItem";

import {
  useDeleteNotification,
  useUpdateNotification
} from "@/src/queries/notifications/notificationQueries";
import { UserNotificationDTO } from "@/src/types/types";
import { useResetSearchTerms } from "@/src/utils/searchTermUtils";

import { useSetAtom } from "jotai";

interface NotificationPanelProps {
  notifications: UserNotificationDTO[];
  /** close handler if inside Menu component */
  handleCloseMenu?: () => void;
  isMobile: boolean;
}

const NotificationPanel = ({
  notifications,
  handleCloseMenu,
  isMobile
}: NotificationPanelProps) => {
  const navigate = useNavigate();
  const setToastNotification = useSetAtom(notificationAtom);
  const resetSearchTerms = useResetSearchTerms();
  const { mutate: updateNotification } = useUpdateNotification();
  const { mutate: deleteNotification } = useDeleteNotification();

  const [secondaryMenuAnchorEl, setSecondaryMenuAnchorEl] = useState<null | {
    element: HTMLElement;
    notification: UserNotificationDTO;
  }>(null);
  const [showAll, setShowAll] = useState<boolean>(false);
  const effectiveShowAll = isMobile || showAll;

  const secondaryMenuOpen = Boolean(secondaryMenuAnchorEl);
  const unreadNotifications = notifications.filter(({ isRead }) => !isRead);

  const handleCloseSecondaryMenu = () => {
    setSecondaryMenuAnchorEl(null);
  };

  const handleNavigation = (url: string) => {
    navigate(url);
  };

  const handleMenuItemClick = (
    notificationId: number,
    isRead: boolean,
    actionUrl?: string,
    contentData?: { auctionId?: number }
  ) => {
    try {
      if (!isRead) {
        updateNotification({ notificationId, isRead: true });
      }
      if (actionUrl) {
        // If actionUrl is /my-auctions and we have an auctionId, navigate to the specific auction page
        let finalUrl = actionUrl;
        if (
          actionUrl === "/my-auctions" &&
          contentData?.auctionId !== undefined
        ) {
          finalUrl = `/my-auctions/${contentData.auctionId}`;
        }
        handleNavigation(finalUrl);
        handleCloseMenu?.();
        resetSearchTerms();
      }
    } catch (error) {
      setToastNotification({
        text: t("components.notifications.generalFail"),
        variant: "error"
      });
    }
  };

  const handleDeleteNotification = (notificationId: number) => {
    try {
      deleteNotification(notificationId);
      handleCloseSecondaryMenu();
    } catch (error) {
      setToastNotification({
        text: t("components.notifications.deleteFailed"),
        variant: "error"
      });
    }
  };

  const handleMarkAllAsRead = () => {
    try {
      for (const { id } of unreadNotifications) {
        updateNotification({ notificationId: id, isRead: true });
      }
    } catch (error) {
      setToastNotification({
        text: t("components.notifications.updateFailed"),
        variant: "error"
      });
    }
  };

  const handleMarkReadUnread = (notificationId: number, isRead: boolean) => {
    try {
      updateNotification({ notificationId, isRead });
      handleCloseSecondaryMenu();
    } catch (error) {
      setToastNotification({
        text: t("components.notifications.updateFailed"),
        variant: "error"
      });
    }
  };

  const visibleNotifications = effectiveShowAll
    ? notifications
    : notifications.slice(0, 7);
  return (
    <>
      <Box
        sx={{
          width: isMobile ? "100%" : 560,
          height: isMobile ? "100%" : 871,
          ...(!isMobile && { overflowY: "auto" })
        }}
      >
        <Box sx={{ mb: 2, mr: 2, ml: 2 }}>
          <Typography
            variant="h2"
            sx={{
              display: "block",
              fontStyle: "italic"
            }}
          >
            {t("components.notifications.notifications")}
          </Typography>
          <Button
            sx={{ textTransform: "none", px: 1.25, ml: -1 }}
            onClick={handleMarkAllAsRead}
            variant="text"
            color="primary"
          >
            {t("components.notifications.markAllRead")}
          </Button>
        </Box>
        <List sx={{ p: 0 }}>
          {visibleNotifications.length ? (
            visibleNotifications.map((notification, index) => (
              <ListItemButton
                sx={{
                  display: "flex",
                  position: "relative",
                  minHeight: "106px",
                  padding: "16px",
                  cursor: "pointer"
                }}
                selected={!notification.isRead}
                key={notification.id}
                data-testid={`notification-item-${index}`}
              >
                <NotificationItem
                  handleClick={(id, isRead, url, contentData) =>
                    handleMenuItemClick(id, isRead, url, contentData)
                  }
                  setSecondaryMenuAnchorEl={setSecondaryMenuAnchorEl}
                  notification={notification}
                />
              </ListItemButton>
            ))
          ) : (
            <ListItemButton disabled>
              <ListItemText
                primary={t("components.notifications.noNotifications")}
              />
            </ListItemButton>
          )}
        </List>
        {!isMobile && !showAll && notifications.length > 7 && (
          <Button
            sx={{ textTransform: "none", mr: "auto", ml: "auto" }}
            onClick={() => setShowAll(true)}
            variant="text"
            color="primary"
          >
            {t("general.showAll")}
          </Button>
        )}
      </Box>
      <Menu
        anchorEl={secondaryMenuAnchorEl?.element}
        open={secondaryMenuOpen}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={handleCloseSecondaryMenu}
      >
        <MenuItem
          onClick={() => {
            if (secondaryMenuAnchorEl) {
              const { id, isRead } = secondaryMenuAnchorEl.notification;
              handleMarkReadUnread(id, !isRead);
            }
          }}
        >
          <ListItemIcon>
            <Check fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {secondaryMenuAnchorEl?.notification.isRead
              ? t("components.notifications.markUnread")
              : t("components.notifications.markRead")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() =>
            secondaryMenuAnchorEl &&
            handleDeleteNotification(secondaryMenuAnchorEl?.notification.id)
          }
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("general.delete")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default NotificationPanel;
